import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPut } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import Modal from '../components/ui/Modal';

const MAINT_KEY = 'maintenance';
const VERSION_KEY = 'force_refresh';

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '13px 0', borderBottom: '1px solid #26262B' }}>
    <span style={{ fontSize: 13, color: '#A1A1AA' }}>{label}</span>
    <span style={{ fontSize: 13.5, color: '#FAFAFA', fontFamily: 'monospace' }}>{value || '-'}</span>
  </div>
);

export default function SettingsPage() {
  const { user } = useAuth();

  // Maintenance mode (stored as the `maintenance` app-setting)
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('We’ll be right back');
  const [message, setMessage] = useState('NexCarat is undergoing scheduled maintenance. Please check back shortly.');
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState('');
  const [error, setError] = useState('');

  // Force refresh: bumping this version string makes every open NexCarat session show a
  // "new version available, please refresh" prompt within ~2 minutes.
  const [lastForced, setLastForced] = useState(null);
  const [forcing, setForcing] = useState(false);
  const [forceOk, setForceOk] = useState('');
  const [forceErr, setForceErr] = useState('');
  const [confirmForce, setConfirmForce] = useState(false);

  const load = useCallback(async () => {
    try {
      const doc = await apiGet(ENDPOINTS.ADMIN.SETTING(MAINT_KEY));
      const v = doc?.value || {};
      setEnabled(!!v.enabled);
      if (v.title) setTitle(v.title);
      if (v.message) setMessage(v.message);
    } catch { /* no maintenance doc yet - keep defaults */ }
    try {
      const doc = await apiGet(ENDPOINTS.ADMIN.SETTING(VERSION_KEY));
      if (doc?.value?.version) setLastForced(doc.value.version);
    } catch { /* never forced yet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const forceRefresh = async () => {
    setConfirmForce(false);
    setForcing(true); setForceOk(''); setForceErr('');
    const version = new Date().toISOString();
    try {
      await apiPut(ENDPOINTS.ADMIN.SETTING(VERSION_KEY), { value: { version } });
      setLastForced(version);
      setForceOk('Done. All users will be prompted to refresh shortly.');
    } catch (err) { setForceErr(err.message || 'Could not trigger the refresh.'); }
    finally { setForcing(false); }
  };

  const save = async (nextEnabled) => {
    setSaving(true); setOk(''); setError('');
    const en = nextEnabled != null ? nextEnabled : enabled;
    try {
      await apiPut(ENDPOINTS.ADMIN.SETTING(MAINT_KEY), { value: { enabled: en, title: title.trim(), message: message.trim() } });
      setEnabled(en);
      setOk(en ? 'Maintenance mode is ON - users are blocked.' : 'Maintenance mode is OFF.');
    } catch (err) { setError(err.message || 'Could not save.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Settings</strong></div>
        <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
          <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Settings</em>
        </h1>
        <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}>Your admin account and platform maintenance.</p>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}
      {ok && <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 8, color: '#22C55E', fontSize: 13, marginBottom: 14 }}>{ok}</div>}

      {/* Maintenance mode */}
      <div className="flex items-center justify-between gap-4" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 16 }}>
        <div>
          <div className="card-title">Maintenance mode</div>
          <div style={{ fontSize: 12, color: '#A1A1AA', marginTop: 4 }}>When ON, every NexCarat user is blocked by a full-screen notice right after login.</div>
        </div>
        <button type="button" className={`toggle${enabled ? ' on' : ''}`} onClick={() => save(!enabled)} disabled={saving} />
      </div>
      <div style={{ maxWidth: 620, marginBottom: 12 }}>
        <div className="field" style={{ marginBottom: 12 }}>
          <label className="field-label">Notice title</label>
          <input className="field-input" value={title} onChange={e => { setTitle(e.target.value); setOk(''); }} placeholder="We'll be right back" />
        </div>
        <div className="field">
          <label className="field-label">Notice message</label>
          <textarea className="field-input" value={message} onChange={e => { setMessage(e.target.value); setOk(''); }} rows={3} style={{ resize: 'vertical' }} placeholder="What users see during maintenance" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : 'Save notice'}</button>
          <span style={{ fontSize: 12, color: enabled ? '#F59E0B' : '#71717A' }}>{enabled ? 'Currently ON' : 'Currently off'}</span>
        </div>
      </div>

      {/* Force refresh */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 6, marginTop: 32 }}>App version</div>
      <div style={{ maxWidth: 620 }}>
        <div style={{ fontSize: 12, color: '#A1A1AA', margin: '4px 0 14px', lineHeight: 1.6 }}>
          After deploying a new build, use this to make every signed-in user's app show a "new version available" prompt and refresh, instead of silently running stale code.
        </div>
        {forceErr && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 12 }}>{forceErr}</div>}
        {forceOk && <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 8, color: '#22C55E', fontSize: 13, marginBottom: 12 }}>{forceOk}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setConfirmForce(true)} disabled={forcing}>{forcing ? 'Triggering…' : 'Force refresh for all users'}</button>
          <span style={{ fontSize: 12, color: '#71717A' }}>
            {lastForced ? `Last triggered ${new Date(lastForced).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : 'Never triggered yet'}
          </span>
        </div>
      </div>

      {/* Admin account */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 6, marginTop: 32 }}>Admin account</div>
      <div style={{ maxWidth: 560 }}>
        <Row label="Name" value={[user?.first_name, user?.last_name].filter(Boolean).join(' ')} />
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={(user?.role || '').replace('_', ' ')} />
        <Row label="Platform admin" value={user?.is_admin ? 'Yes' : 'No'} />
      </div>

      <Modal open={confirmForce} onClose={() => setConfirmForce(false)} title="Force refresh for all users" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 13, color: '#A1A1AA', margin: 0, lineHeight: 1.6 }}>Force every signed-in NexCarat user to refresh their app? They will see a "new version available" prompt within about 2 minutes.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setConfirmForce(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={forceRefresh}>Force refresh</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
